import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CallCenterProvider } from "../../lib/callCenter";
import { EmailPreviewProvider } from "../../lib/emailPreview";
import CallDock from "../call/CallDock";
import DialerFAB from "../call/DialerFAB";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";

export default function AppShell() {
    const { user, loading } = useAuth();
    const [unread, setUnread] = useState(0);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    // Track notification IDs we've already toasted this session — prevents
    // re-toasting the same "new offer" toast on every 30-second poll.
    const toastedIdsRef = useRef(new Set());
    // Track whether we've completed the first poll. We don't want to toast
    // the user's HISTORICAL backlog on page load — only NEW ones discovered
    // after the app boots.
    const firstPollDoneRef = useRef(false);

    // Keep `navigate` in a ref so it doesn't reset the polling interval on
    // every render — useNavigate returns a function whose identity can be
    // unstable across renders depending on React Router internals.
    const navigateRef = useRef(navigate);
    useEffect(() => { navigateRef.current = navigate; }, [navigate]);

    useEffect(() => {
        let active = true;
        async function fetchUnread() {
            try {
                const { data } = await api.get("/notifications");
                if (!active) return;
                setUnread(data.filter((n) => !n.read && !n.is_obsolete).length);

                // Emit a discrete in-app toast for any NEW `new_offer_assigned`
                // notification that just arrived. We only toast AFTER the
                // first poll — otherwise users would be spammed by historical
                // notifications on every page refresh.
                if (firstPollDoneRef.current) {
                    const fresh = data.filter(
                        (n) =>
                            n.type === "new_offer_assigned"
                            && !n.read
                            && !n.is_obsolete
                            && !toastedIdsRef.current.has(n.id),
                    );
                    for (const n of fresh) {
                        toastedIdsRef.current.add(n.id);
                        toast.success(n.title || "Nouvelle offre", {
                            description: n.message,
                            duration: 7000,
                            action: {
                                label: "Voir",
                                onClick: () => navigateRef.current("/offres"),
                            },
                        });
                    }
                } else {
                    // Mark all currently-present notifs as already-toasted so
                    // we don't replay them when the polling loop activates.
                    for (const n of data) {
                        if (n.type === "new_offer_assigned") toastedIdsRef.current.add(n.id);
                    }
                    firstPollDoneRef.current = true;
                }
            } catch {
                /* ignore */
            }
        }
        if (user) {
            fetchUnread();
            const id = setInterval(fetchUnread, 30000);
            return () => {
                active = false;
                clearInterval(id);
            };
        }
        return () => {
            active = false;
        };
    }, [user]);

    // Close the mobile drawer whenever the route changes
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location.pathname]);

    if (loading) {
        return (
            <div className="min-h-screen bg-shariti-cream flex items-center justify-center">
                <div className="animate-pulse text-slate-400 font-bold">Chargement…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;

    return (
        <CallCenterProvider>
            <EmailPreviewProvider>
                <div className="min-h-screen bg-shariti-cream flex">
                    <Sidebar
                        mobileOpen={mobileNavOpen}
                        onMobileClose={() => setMobileNavOpen(false)}
                    />
                    <div className="flex-1 min-w-0 flex flex-col">
                        <Topbar
                            unreadCount={unread}
                            onUnreadChange={setUnread}
                            onOpenMobileNav={() => setMobileNavOpen(true)}
                        />
                        <main className="flex-1 px-4 md:px-8 py-4 md:py-10 max-w-[1600px] w-full mx-auto">
                            <Outlet />
                        </main>
                    </div>
                    <CallDock />
                    <DialerFAB />
                </div>
            </EmailPreviewProvider>
        </CallCenterProvider>
    );
}
